import { useEffect } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { applyPageMeta } from '../lib/seo';
import { PAGE_META } from '../lib/routeMeta';
import { useViewerCountry } from '../lib/geo';
import { SweepstakesRules } from '../content/sweepstakesRules';
import { HeadingUnderscore } from '../components/register/RegisterLayout';
import { NonUsEligibilityNotice } from '../components/NonUsEligibilityNotice';
import { Button } from '../components/Button';

/**
 * /sweepstakes — static posting of the "State Farm Gamerhood Chair Giveaway —
 * Official Rules" sweepstakes document (per GHS5-80). Not linked from anywhere
 * on the site and intentionally omitted from the sitemap; it exists so the
 * rules can be reached at the URL printed in the promotion.
 *
 * Layout mirrors RulesPage (same deep-brand section + lilac gradient
 * Infoback card + diagonal pattern, same non-US eligibility band) but WITHOUT
 * the week selector / event-state fetch — this is a single fixed document, not
 * the per-week Contest rules. Body content lives in content/sweepstakesRules.tsx
 * (verbatim transcription).
 */
export default function SweepstakesPage() {
  const reduce = useReducedMotion();
  const { country } = useViewerCountry();

  useEffect(() => {
    applyPageMeta(PAGE_META.sweepstakes);
  }, []);

  const showNonUsNotice = country !== null && country !== 'US';

  return (
    <section className="relative isolate flex w-full flex-1 flex-col items-center overflow-hidden px-[28px] pb-[60px] pt-[60px] sm:px-[40px] sm:pt-[80px] md:px-[64px]">
      <div className="relative flex w-full max-w-[1030px] flex-col gap-[20px]">
        <h1 className="font-['Orbitron',sans-serif] text-[clamp(28px,7vw,46px)] uppercase leading-[0.95] tracking-[-0.03em] text-white">
          giveaway rules
          <HeadingUnderscore />
        </h1>

        <motion.div
          initial={reduce ? false : { opacity: 0, y: 16 }}
          animate={reduce ? undefined : { opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
          className="relative min-h-[450px] overflow-hidden"
          style={{
            backgroundImage:
              "linear-gradient(180deg, #413850 0%, #8c05ea 100%), url('/assets/patterns/diagonal.svg')",
            backgroundBlendMode: 'overlay',
          }}
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 opacity-[0.16]"
            style={{
              backgroundImage: "url('/assets/patterns/diagonal.svg')",
              backgroundRepeat: 'repeat',
            }}
          />

          <div className="relative flex min-h-[450px] flex-col items-stretch gap-[30px] px-[20px] py-[40px] sm:px-[60px] md:px-[100px]">
            {showNonUsNotice && <NonUsEligibilityNotice />}

            <SweepstakesRules />

            <div className="mt-auto flex justify-center pt-[10px]">
              <Button variant="dark" to="/" className="w-[200px]">
                Home
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
